import {
  ENROLLMENT_REQUIRED_SIGNERS,
  ENROLLMENT_STATUS,
  MULTISIG_ORG_STATUS,
  MULTISIG_SIGNER_ROLE,
  MULTISIG_SIGNER_STATUS,
} from "@/constants/multisigEnrollment";

import { useMultisigEnrollment } from "@/apiQueries/multisigEnrollment/useMultisigEnrollment";
import { useOrganizationMultisig } from "@/apiQueries/multisigEnrollment/useOrganizationMultisig";
import { useUsers } from "@/apiQueries/useUsers";

import { toActiveSigners } from "@/helpers/multisigSigners";

import type {
  ApiOrganizationMultisig,
  ApiUser,
  CoSigningStatus,
  MultisigEnrollmentSession,
  UserRole,
} from "@/types";

const mySessionKey = (
  session: MultisigEnrollmentSession | null | undefined,
  userId: string,
): string | undefined => {
  if (!session || !userId) return undefined;
  if (session.initiator_user_id === userId) return session.owner1_public_key ?? undefined;
  if (session.owner2_user_id === userId) return session.owner2_public_key ?? undefined;
  return undefined;
};

export const composeCoSigningStatus = ({
  org,
  users,
  enrollment,
  userId,
  role,
}: {
  org: ApiOrganizationMultisig | undefined;
  users: ApiUser[] | undefined;
  enrollment: MultisigEnrollmentSession | null | undefined;
  userId: string;
  role: UserRole | null;
}): CoSigningStatus => {
  const activeSigners = toActiveSigners(users);
  const mySigner = userId ? activeSigners.find((s) => s.user_id === userId) : undefined;
  const session = enrollment ?? null;

  const pendingSigner =
    session && session.status === ENROLLMENT_STATUS.PENDING_OWNER2_KEY
      ? {
          user_id: session.owner2_user_id,
          first_name: "",
          last_name: "",
          role: MULTISIG_SIGNER_ROLE.OWNER,
        }
      : undefined;

  return {
    org: {
      multisig_status: org?.multisig_status ?? MULTISIG_ORG_STATUS.BACKEND_ONLY,
      governance_mode: org?.multisig_governance_mode ?? null,
      required_signers: ENROLLMENT_REQUIRED_SIGNERS,
      active_signers: activeSigners,
      pending_signer: pendingSigner,
      invited_signers: [],
    },
    me: {
      user_id: userId || undefined,
      role,
      signer_status: mySigner ? MULTISIG_SIGNER_STATUS.ACTIVE : MULTISIG_SIGNER_STATUS.NONE,
      stellar_public_key: mySigner?.stellar_public_key ?? mySessionKey(session, userId),
    },
    enrollment: session,
  };
};

export const useCoSigningStatus = ({ userId, role }: { userId: string; role: UserRole | null }) => {
  const isOwner = role === "owner";

  const orgQuery = useOrganizationMultisig();
  const enrollmentQuery = useMultisigEnrollment({ enabled: isOwner });
  const usersQuery = useUsers({ enabled: isOwner });

  const isReady =
    orgQuery.isSuccess && (!isOwner || (enrollmentQuery.isSuccess && usersQuery.isSuccess));
  const data: CoSigningStatus | undefined = isReady
    ? composeCoSigningStatus({
        org: orgQuery.data,
        users: usersQuery.data,
        enrollment: enrollmentQuery.data,
        userId,
        role,
      })
    : undefined;

  return {
    data,
    isLoading:
      orgQuery.isLoading || (isOwner && (enrollmentQuery.isLoading || usersQuery.isLoading)),
    isFetching: orgQuery.isFetching || enrollmentQuery.isFetching || usersQuery.isFetching,
    isError: orgQuery.isError || enrollmentQuery.isError || usersQuery.isError,
    error: orgQuery.error ?? enrollmentQuery.error ?? usersQuery.error ?? null,
    // `refetch()` runs a query even while it is disabled, so a non-Owner must
    // not be sent to the Owner-only endpoints (they would 403 into an error).
    refetch: () =>
      isOwner
        ? Promise.all([orgQuery.refetch(), enrollmentQuery.refetch(), usersQuery.refetch()])
        : Promise.all([orgQuery.refetch()]),
  };
};
